/* eslint-disable no-alert */
/* eslint-disable no-restricted-globals */
import React, { useMemo, useState } from 'react';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import {
  Row, Col, Card, CardBody,
  Nav, NavItem, NavLink, TabContent, TabPane,
  Button, Form, Input, Label,
} from 'reactstrap';
import moment from 'moment';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import classnames from 'classnames';

import { Can } from '../../utilities/Can';
import BaseGrid from '../../components/Common/BaseGrid';
import SaleFormModal from '../../components/Sale/SaleFormModal';
import TagSelector from '../../components/Common/TagSelector';
import { post } from '../../helpers/api_helper';

import { changeSidebarType } from '../../store/actions';

const DISTRIBUTION_ERROR_FILTERS = 'manually_concluded_at=null&auto_distribution_error_at=notnull&canceled_at=null&loadavailable=1';
const PLATFORM_OPTIONS = ['BigCommerce', 'Rithum', 'Partner API', 'Fidelitone', 'Manual'];

const buildFilterString = (filters) => {
  const params = new URLSearchParams();

  if (filters.date) {
    params.set('s.created_at', filters.date);
  }

  if (filters.billingName.trim() !== '') {
    params.set('billing_name', filters.billingName.trim());
  }

  if (filters.shippingName.trim() !== '') {
    params.set('shipping_name', filters.shippingName.trim());
  }

  if (filters.platform.trim() !== '') {
    params.set('platform', filters.platform.trim());
  }

  const tagIds = filters.tags
    .map((tag) => tag.id)
    .filter((id) => id != null);

  if (tagIds.length > 0) {
    params.set('listing_tag.tag_id', tagIds.join(','));
  }

  return params.toString();
};

const mergeFilterStrings = (...filters) => filters
  .filter((filter) => filter != null && filter !== '')
  .join('&');

function Sales(props) {
  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
    timeOut: 8000,
  };

  const { t } = props;

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalSale, setEditModalSale] = useState({});
  const [signal, setSignal] = useState(0);
  const [activeTab, setActiveTab] = useState('1');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [draftFilters, setDraftFilters] = useState({
    date: '',
    billingName: '',
    shippingName: '',
    platform: '',
    tags: [],
  });
  const [appliedFilters, setAppliedFilters] = useState({
    date: '',
    billingName: '',
    shippingName: '',
    platform: '',
    tags: [],
  });

  const allSalesFilters = useMemo(
    () => buildFilterString(appliedFilters),
    [appliedFilters],
  );

  const distributionErrorFilters = useMemo(
    () => mergeFilterStrings(DISTRIBUTION_ERROR_FILTERS, allSalesFilters),
    [allSalesFilters],
  );

  const editSale = (sale) => {
    setEditModalSale(sale);
    setEditModalOpen(true);
  };

  const onEditSuccess = () => {
    setSignal(signal + 1);
    setEditModalOpen(false);
  };

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const sendChannelMessage = async (channel, msg) => {
    try {
      await post(`/api/slack/message/channel/${channel}`, { msg });
    } catch (e) {
      toastr.error(e.message);
    }
  };

  const concludeSale = async (sale) => {
    try {
      if (sale.platform === 'Partner API') {
        if (confirm(t('Are you sure you want to conclude this sale?'))) {
          await post(`/api/sales/save/${sale.id}`, { manually_concluded_at: (new Date(Date.now())).toISOString() });

          setSignal(signal + 1);
        }
      } else if (confirm(t('Send Slack message to manually conclude this sale?'))) {
        sendChannelMessage('shipping', `Please cancel/manual conclude ${sale.platform} sale ${sale.reference1} - ${sale.reference2}`);
      }
    } catch (error) {
      toastr.error(error.message);
    }
  };

  const applyAdvancedSearch = () => {
    setAppliedFilters({
      date: draftFilters.date,
      billingName: draftFilters.billingName,
      shippingName: draftFilters.shippingName,
      platform: draftFilters.platform,
      tags: [...draftFilters.tags],
    });
  };

  const resetAdvancedSearch = () => {
    const emptyFilters = {
      date: '',
      billingName: '',
      shippingName: '',
      platform: '',
      tags: [],
    };

    setDraftFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Nav tabs style={{ borderBottom: 'none' }}>
          <NavItem style={{ borderBottom: 'none' }}>
            <NavLink
              style={{ cursor: 'pointer' }}
              className={classnames({
                active: activeTab === '1',
                'bg-warning': activeTab === '1',
                'text-muted': activeTab !== '1',
                'text-dark': activeTab === '1',
              })}
              onClick={() => {
                toggle('1');
              }}
            >
              {t('All Sales')}
            </NavLink>
          </NavItem>
          <NavItem style={{ borderBottom: 'none' }}>
            <NavLink
              style={{ cursor: 'pointer' }}
              className={classnames({
                active: activeTab === '2',
                'bg-warning': activeTab === '2',
                'text-muted': activeTab !== '2',
                'text-dark': activeTab === '2',
              })}
              onClick={() => {
                toggle('2');
              }}
            >
              {t('Distribution Errors')}
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          <TabPane tabId="1">
            <Row>
              <Col xxl="12">
                <Card>
                  <CardBody>
                    <Can i="see" this="Sales">
                      <div className="mb-3">
                        <div className="d-flex flex-wrap gap-2 align-items-center">
                          <Button
                            color={showAdvancedSearch ? 'soft-warning' : 'soft-dark'}
                            className={showAdvancedSearch ? 'text-dark' : 'text-body'}
                            size="sm"
                            onClick={() => { setShowAdvancedSearch((prev) => !prev); }}
                          >
                            <span className="bx bx-filter-alt" />
                            {' '}
                            {t('Advanced Search')}
                          </Button>

                          {allSalesFilters !== '' && (
                            <Button
                              color="light"
                              size="sm"
                              onClick={resetAdvancedSearch}
                            >
                              <span className="mdi mdi-close-thick" />
                              {' '}
                              {t('Clear Filters')}
                            </Button>
                          )}
                        </div>

                        {showAdvancedSearch && (
                          <Row>
                            <Col md="6">
                              <Form
                                className="border rounded p-3 mt-3 bg-light"
                                onSubmit={(e) => {
                                  e.preventDefault();
                                  applyAdvancedSearch();
                                }}
                              >
                                <Row className="g-3">
                                  <Col md="3">
                                    <Label for="sales-created-at-filter">{t('Date')}</Label>
                                    <Input
                                      id="sales-created-at-filter"
                                      type="date"
                                      value={draftFilters.date}
                                      onChange={(e) => {
                                        setDraftFilters((prev) => ({
                                          ...prev,
                                          date: e.target.value,
                                        }));
                                      }}
                                    />
                                  </Col>

                                  <Col md="3">
                                    <Label for="sales-billing-name-filter">{t('Billing Name')}</Label>
                                    <Input
                                      id="sales-billing-name-filter"
                                      type="text"
                                      placeholder={t('Billing name')}
                                      value={draftFilters.billingName}
                                      onChange={(e) => {
                                        setDraftFilters((prev) => ({
                                          ...prev,
                                          billingName: e.target.value,
                                        }));
                                      }}
                                    />
                                  </Col>

                                  <Col md="3">
                                    <Label for="sales-shipping-name-filter">{t('Shipping Name')}</Label>
                                    <Input
                                      id="sales-shipping-name-filter"
                                      type="text"
                                      placeholder={t('Shipping name')}
                                      value={draftFilters.shippingName}
                                      onChange={(e) => {
                                        setDraftFilters((prev) => ({
                                          ...prev,
                                          shippingName: e.target.value,
                                        }));
                                      }}
                                    />
                                  </Col>

                                  <Col md="3">
                                    <Label for="sales-platform-filter">{t('Platform')}</Label>
                                    <Input
                                      id="sales-platform-filter"
                                      type="select"
                                      value={draftFilters.platform}
                                      onChange={(e) => {
                                        setDraftFilters((prev) => ({
                                          ...prev,
                                          platform: e.target.value,
                                        }));
                                      }}
                                    >
                                      <option value="">{t('All Platforms')}</option>
                                      {PLATFORM_OPTIONS.map((option) => (
                                        <option key={option} value={option}>
                                          {option}
                                        </option>
                                      ))}
                                    </Input>
                                  </Col>

                                  <Col md="6">
                                    <Label>{t('Listing Tags')}</Label>
                                    <TagSelector
                                      id="sales-advanced-tag-filter"
                                      initialTags={draftFilters.tags}
                                      onUpdate={(tags) => {
                                        setDraftFilters((prev) => ({
                                          ...prev,
                                          tags,
                                        }));
                                      }}
                                    />
                                  </Col>
                                </Row>

                                <div className="d-flex gap-2 mt-3">
                                  <Button color="primary" size="sm" type="submit">
                                    {t('Apply Filters')}
                                  </Button>
                                  <Button color="light" size="sm" type="button" onClick={resetAdvancedSearch}>
                                    {t('Reset')}
                                  </Button>
                                </div>
                              </Form>
                            </Col>
                          </Row>
                        )}
                      </div>

                      <BaseGrid
                        url="/api/sales"
                        filters={allSalesFilters}
                        shouldUpdate={signal}
                        create={() => { setEditModalOpen(true); }}
                        className="table table-sm table-hover table-striped table-bordered fs-6"
                        hasSearch
                        config={
                          {
                            columns: {
                              id: {
                                label: 'ID',
                                sort: true,
                                link: {
                                  to: '/sales/%id%',
                                },
                              },
                              paid_at: {
                                label: 'Paid',
                                date: {
                                  format: (date) => (moment(date).fromNow()),
                                  timezone: process.env.LOCAL_TZ,
                                },
                                sort: true,
                                style: { fontSize: '11px' },
                              },
                              created_at: {
                                label: 'Imported',
                                date: {
                                  format: (date) => (moment(date).fromNow()),
                                  timezone: process.env.LOCAL_TZ,
                                },
                                sort: true,
                                style: { fontSize: '11px' },
                              },
                              reference1: {
                                label: 'Ref 1',
                                html: true,
                                sort: true,
                                format: (e, x) => {
                                  if (x.canceled_at !== null) {
                                    return `<strike class="text-danger">${x.reference1}</strike>`;
                                  }

                                  return x.reference1;
                                },
                              },
                              reference2: { label: 'Ref 2', sort: true, style: { fontSize: '10px' } },
                              platform: { label: 'Platform', sort: false, style: { fontSize: '10px' } },
                              has_invoice: {
                                label: 'Inv',
                                sort: false,
                                translate: false,
                                class: 'text-center',
                                style: { width: '50px', fontSize: '14px', color: '#198754' },
                                format: (value) => (Number(value) > 0 ? '✓' : ''),
                              },
                              site: {
                                label: 'Site',
                                sort: false,
                                style: { fontSize: '10px' },
                                filter: (row) => ({
                                  site: row.site,
                                }),
                              },
                              // customer_email: { label: 'Email', sort: true },
                              billing_name: {
                                label: 'Billing Name',
                                sort: true,
                                format: (e, x) => (`${x.billing_firstname} ${x.billing_lastname}`),
                                style: { fontSize: '11px' },
                              },
                              total_paid: {
                                label: 'Total Paid',
                                sort: true,
                                style: { minWidth: '100px', fontSize: '10px' },
                                format: (e, x) => (`${x.total_paid} ${x.currency_code}`),
                              },
                              // eslint-disable-next-line max-len
                              // customer_note: { label: 'Customer Note', sort: false, style: { width: '150px' } },
                              staff_note: {
                                label: 'Staff Note',
                                sort: false,
                                class: 'text-danger',
                                format: (f) => (f !== null ? f.substring(0, 40) : ''),
                                style: { minWidth: '150px', fontSize: '9px' },
                              },
                              preferred_ship_method: {
                                label: 'Pref. Shp Mthd',
                                sort: false,
                                style: { minWidth: '150px', fontSize: '9px' },
                                format: (x) => (x !== null ? x.split(' - ')[0].substring(0, 20) : ''),
                              },
                              items: {
                                label: props.t('Items'),
                                format: (x) => (
                                  x.reduce((a, c) => a + c.qty, 0)
                                ),
                              },
                              actions: {
                                label: props.t('Actions'),
                                actions: [
                                  {
                                    key: 'edit',
                                    icon: 'bx bx-pencil',
                                    class: 'btn btn-sm btn-soft-dark',
                                    alt: props.t('Edit'),
                                    click: editSale,
                                  },
                                ],
                              },
                            },
                            pagination: {
                              sort: 'id',
                              direction: 'desc',
                              perPage: 25,
                            },
                          }
                        }
                      />

                      <SaleFormModal
                        shown={editModalOpen}
                        sale={editModalSale}
                        onSuccess={onEditSuccess}
                        onClose={() => { setEditModalOpen(false); }}
                      />
                    </Can>

                    <Can not i="see" this="Sales"><p>{props.t('Unauthorized')}</p></Can>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </TabPane>
          <TabPane tabId="2">
            <Row>
              <Col xxl="12">
                <Card>
                  <CardBody>
                    <Can i="see" this="Sales">
                      <BaseGrid
                        url="/api/sales"
                        filters={distributionErrorFilters}
                        shouldUpdate={signal}
                        create={() => { setEditModalOpen(true); }}
                        className="table table-sm table-hover table-striped table-bordered fs-6"
                        hasSearch
                        config={
                          {
                            columns: {
                              id: {
                                label: 'ID',
                                sort: true,
                                link: {
                                  to: '/sales/%id%',
                                },
                              },
                              created_at: {
                                label: 'Imported',
                                date: {
                                  format: (date) => (moment(date).fromNow()),
                                  timezone: process.env.LOCAL_TZ,
                                },
                                sort: true,
                                style: { fontSize: '11px' },
                              },
                              reference1: {
                                label: 'Ref 1',
                                html: true,
                                sort: true,
                                format: (e, x) => {
                                  if (x.canceled_at !== null) {
                                    return `<strike class="text-danger">${x.reference1}</strike>`;
                                  }

                                  return x.reference1;
                                },
                              },
                              reference2: { label: 'Ref 2', sort: true, style: { fontSize: '10px' } },
                              platform: { label: 'Platform', sort: false, style: { fontSize: '10px' } },
                              site: { label: 'Site', sort: false, style: { fontSize: '10px' } },
                              // customer_email: { label: 'Email', sort: true },
                              billing_name: {
                                label: 'Billing Name',
                                sort: true,
                                format: (e, x) => (`${x.billing_firstname} ${x.billing_lastname}`),
                                style: { fontSize: '11px' },
                              },
                              items: {
                                label: 'Items',
                                html: true,
                                format: (listings) => {
                                  let html = '';

                                  listings.forEach((listing) => {
                                    html += `<div class="${listing.available > 0 ? '' : 'text-danger'}">${listing.listing.sku}: (need: ${listing.qty}) ${listing.available} avail.</div>`;
                                  });

                                  return html;
                                },
                              },
                              // eslint-disable-next-line max-len
                              // customer_note: { label: 'Customer Note', sort: false, style: { width: '150px' } },
                              staff_note: {
                                label: 'Staff Note',
                                sort: false,
                                class: 'text-danger',
                                style: { minWidth: '150px', fontSize: '9px' },
                              },
                              preferred_ship_method: {
                                label: 'Pref. Shp Mthd',
                                sort: false,
                                style: { minWidth: '150px', fontSize: '9px' },
                                format: (x) => (x !== null ? x.split(' - ')[0].substring(0, 20) : ''),
                              },
                              actions: {
                                label: props.t('Actions'),
                                actions: [
                                  {
                                    key: 'remove',
                                    icon: 'mdi mdi-close-thick',
                                    class: 'btn btn-sm btn-soft-dark',
                                    alt: props.t('Cancel'),
                                    click: concludeSale,
                                  },
                                ],
                              },
                            },
                            pagination: {
                              sort: 'id',
                              direction: 'desc',
                              perPage: 25,
                            },
                          }
                        }
                      />
                    </Can>

                    <Can not i="see" this="Sales"><p>{props.t('Unauthorized')}</p></Can>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </TabPane>
        </TabContent>
      </div>
    </div>
  );
}

Sales.defaultProps = {
  t: (x) => (x),
};

Sales.propTypes = {
  t: PropTypes.any,
  // changeSidebarType: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({ ...state.Layout });

export default connect(mapStateToProps, {
  changeSidebarType,
})(withTranslation()(Sales));
